// Presentation helpers for backtest verdicts and numbers, shared by the History
// verdict column, the summary bar, and the per-proposal Backtest panel.
import type { BacktestGrade, BacktestProposal, BacktestVerdict } from 'types/history';

// 'neutral' (amber) reads as "resolved, but neither a win nor a loss" on a
// verdict; 'warn' is amber used as an actual caution, and 'info' (cyan) marks a
// solid-but-not-top call. Split so the grade scale runs good → info → warn → bad
// without borrowing the verdict scale's meaning for its middle.
export type VerdictTone = 'good' | 'bad' | 'neutral' | 'warn' | 'info' | 'muted';

const VERDICT_META: Record<BacktestVerdict, { label: string; tone: VerdictTone }> = {
	TP_HIT: { label: 'TP', tone: 'good' },
	SL_HIT: { label: 'SL', tone: 'bad' },
	OPEN: { label: 'OPEN', tone: 'neutral' },
	// Closed by the time stop at neither target nor stop — a real, booked outcome
	// (so not 'muted'), but not a win or a loss in itself.
	TIMEOUT: { label: 'TIMEOUT', tone: 'neutral' },
	NO_ENTRY: { label: 'NO ENTRY', tone: 'neutral' },
	WAIT_SKIPPED: { label: 'WAIT', tone: 'muted' },
	INVALID_PROPOSAL: { label: 'INVALID', tone: 'muted' },
	PENDING_INSUFFICIENT_DATA: { label: 'PENDING', tone: 'muted' },
	DATA_UNAVAILABLE: { label: 'NO DATA', tone: 'muted' },
};
const VERDICT_META_SHORT: Record<BacktestVerdict, { label: string; tone: VerdictTone }> = {
	TP_HIT: { label: 'TP', tone: 'good' },
	SL_HIT: { label: 'SL', tone: 'bad' },
	OPEN: { label: 'OPEN', tone: 'neutral' },
	// Closed by the time stop at neither target nor stop — a real, booked outcome
	// (so not 'muted'), but not a win or a loss in itself.
	TIMEOUT: { label: 'TIMEOUT', tone: 'neutral' },
	NO_ENTRY: { label: 'NE', tone: 'neutral' },
	WAIT_SKIPPED: { label: 'WAIT', tone: 'muted' },
	INVALID_PROPOSAL: { label: 'INVALID', tone: 'muted' },
	PENDING_INSUFFICIENT_DATA: { label: 'P', tone: 'muted' },
	DATA_UNAVAILABLE: { label: 'NO DATA', tone: 'muted' },
};

export function verdictLabel(v: BacktestVerdict | null | undefined): string {
	return v ? (VERDICT_META[v]?.label ?? String(v)) : '—';
}
export function verdictLabelShort(v: BacktestVerdict | null | undefined): string {
	return v ? (VERDICT_META_SHORT[v]?.label ?? String(v)) : '—';
}

export function verdictTone(v: BacktestVerdict | null | undefined): VerdictTone {
	return v ? (VERDICT_META[v]?.tone ?? 'muted') : 'muted';
}

/** Signed R multiple, e.g. "+2.00R" / "-1.00R", or "—" when unknown. */
export function fmtR(r: number | null | undefined): string {
	if (r == null || Number.isNaN(r)) return '—';
	return `${r > 0 ? '+' : ''}${r.toFixed(2)}R`;
}

/** Ratio 0..1 rendered as a percentage, or "—". */
export function fmtPct(x: number | null | undefined): string {
	if (x == null || Number.isNaN(x)) return '—';
	return `${(x * 100).toFixed(1)}%`;
}

// How "decisive" each outcome is — a resolved TP/SL outranks an unfilled or
// waiting one, so a row summarizes to the most conclusive thing that happened
// across all its proposals (surfacing a real win/loss on one proposal that the
// nearest-entry headline would hide behind another's NO ENTRY / WAIT).
//
// Deliberately uses each proposal's IN-WINDOW verdict only. A NO_ENTRY that
// fills *after* the window is drill-down context (shown in the card + on the
// chart), NOT a headline outcome — the setup as specified never triggered in its
// actionable window, so the row must not read as a TP.
//
// TIMEOUT sits with the resolved outcomes rather than the unfilled ones: the
// trade was entered and closed at a known price, so it is a conclusion — just
// not a target or a stop.
const DECISIVENESS: Record<BacktestVerdict, number> = {
	TP_HIT: 7,
	SL_HIT: 6,
	TIMEOUT: 5,
	OPEN: 4,
	NO_ENTRY: 3,
	PENDING_INSUFFICIENT_DATA: 2,
	WAIT_SKIPPED: 1,
	INVALID_PROPOSAL: 0,
	DATA_UNAVAILABLE: 0,
};

/**
 * The proposal an analysis summarizes to — the most decisive in-window outcome,
 * or null when there are none.
 *
 * Returns the whole proposal rather than just its verdict so the row's grade and
 * its verdict are read off the *same* trade: "TP · Best" has to describe one
 * trade, not a TP from the Decent call sitting beside the Best label.
 */
export function decisiveProposal(proposals: BacktestProposal[]): BacktestProposal | null {
	let best: BacktestProposal | null = null;
	let bestRank = -1;
	for (const p of proposals) {
		const rank = DECISIVENESS[p.verdict] ?? 0;
		if (rank > bestRank) {
			bestRank = rank;
			best = p;
		}
	}
	return best;
}

/** The most decisive in-window outcome across an analysis's proposals, or null
 * when there are none. */
export function decisiveVerdict(proposals: BacktestProposal[]): BacktestVerdict | null {
	return decisiveProposal(proposals)?.verdict ?? null;
}

// ─── Trade grades ────────────────────────────────────────────────────────────
// Mirrors the engine's risk labels as normalized by backtest.normalize_grade —
// keep the two in step. The tone runs with the *claim* the grade makes, so a
// breakdown row reads at a glance: Best is a confident call, High Risk is an
// admitted gamble, Wait is not a trade at all.

const GRADE_META: Record<BacktestGrade, { label: string; tone: VerdictTone }> = {
	best: { label: 'Best', tone: 'good' },
	decent: { label: 'Decent', tone: 'info' },
	risk: { label: 'Risk', tone: 'warn' },
	high_risk: { label: 'High Risk', tone: 'bad' },
	wait: { label: 'Wait', tone: 'muted' },
	unknown: { label: 'Other', tone: 'muted' },
};

/** Every grade in report order — best calls first, non-trades last. */
export const GRADE_ORDER: BacktestGrade[] = ['best', 'decent', 'risk', 'high_risk', 'wait', 'unknown'];

export function gradeLabel(g: BacktestGrade | null | undefined): string {
	return g ? (GRADE_META[g]?.label ?? String(g)) : '—';
}

export function gradeTone(g: BacktestGrade | null | undefined): VerdictTone {
	return g ? (GRADE_META[g]?.tone ?? 'muted') : 'muted';
}

/** Abbreviated where a full name would not fit a column head. */
const GRADE_SHORT: Partial<Record<BacktestGrade, string>> = { high_risk: 'H. Risk' };

export function gradeShortLabel(g: BacktestGrade): string {
	return GRADE_SHORT[g] ?? gradeLabel(g);
}

/**
 * A risk label as written by the engine → its slug. The client-side counterpart
 * of backtest.normalize_grade, used only to read rollups stored before the grade
 * columns existed; fresh rows carry `decisive_label` already.
 */
export function normalizeGrade(risk_label: string | null | undefined): BacktestGrade {
	if (!risk_label) return 'unknown';
	const slug = risk_label
		.trim()
		.toLowerCase()
		.replace(/[\s-]+/g, '_');
	return (GRADE_ORDER as string[]).includes(slug) ? (slug as BacktestGrade) : 'unknown';
}

/** The grade a History row headlines with: the stored value, or recomputed from
 *  the proposals for rows written before the column existed. */
export function rollupGrade(rollup: { decisive_label?: BacktestGrade | null; proposals?: BacktestProposal[] }): BacktestGrade | null {
	if (rollup.decisive_label) return rollup.decisive_label;
	const p = decisiveProposal(rollup.proposals ?? []);
	return p ? normalizeGrade(p.risk_label) : null;
}

/** One entry of a row's outcome breakdown: a verdict, the grade of the call that
 *  produced it, and how many proposals landed on exactly that pair. */
export interface VerdictOutcome {
	verdict: BacktestVerdict;
	grade: BacktestGrade;
	count: number;
}

/**
 * Every outcome an analysis produced, most decisive first.
 *
 * An analysis is one row but up to four proposals, and they routinely disagree —
 * two targets hit while a third stops out is an ordinary result. Collapsing that
 * to a single headline verdict hid the losses behind the wins (or the reverse),
 * so the row shows the lot.
 *
 * Proposals that match on *both* verdict and grade are merged with a count:
 * two identical chips side by side read as a rendering bug, while "TP ×2" is the
 * same fact stated once. Anything that differs on either axis stays its own chip
 * — "the Best call won and the Risk call stopped out" is precisely the finding
 * worth seeing.
 */
/**
 * The same outcomes, bucketed by the grade that produced them.
 *
 * What the History table's grade columns render: each column asks "what did our
 * <grade> call do on this analysis?", which is one lookup here. Grades with no
 * proposal are simply absent, so a caller can tell "we made no Risk call" from
 * "our Risk call stopped out".
 */
export function outcomesByGrade(proposals: BacktestProposal[]): Map<BacktestGrade, VerdictOutcome[]> {
	const out = new Map<BacktestGrade, VerdictOutcome[]>();
	for (const o of verdictOutcomes(proposals)) {
		const bucket = out.get(o.grade);
		if (bucket) bucket.push(o);
		else out.set(o.grade, [o]);
	}
	return out;
}

export function verdictOutcomes(proposals: BacktestProposal[]): VerdictOutcome[] {
	const byPair = new Map<string, VerdictOutcome>();
	for (const p of proposals) {
		const grade = normalizeGrade(p.risk_label);
		const key = `${p.verdict}|${grade}`;
		const seen = byPair.get(key);
		if (seen) seen.count += 1;
		else byPair.set(key, { verdict: p.verdict, grade, count: 1 });
	}
	return [...byPair.values()].sort((a, b) => (DECISIVENESS[b.verdict] ?? 0) - (DECISIVENESS[a.verdict] ?? 0));
}

/** Price with magnitude-aware precision so both BTC and micro-caps read cleanly. */
export function fmtPrice(v: number | null | undefined): string {
	if (v == null || Number.isNaN(v)) return '—';
	const a = Math.abs(v);
	let dp = 2;
	if (a < 1) dp = 6;
	else if (a < 100) dp = 4;
	return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: dp });
}
